import React, { useEffect } from "react";
import { observer } from "mobx-react-lite";
import { useWindowSize } from "react-use";
import { useParams } from "react-router-dom";
import { safe_get } from "../../report-visuals";
import { useStore } from "../../helpers";
import _ from "lodash";
import { TaskList } from "./task-list";
import { EditTask } from "./task-edit";
import { CreateTask } from "./task-create";
import { TaskView } from "./task-view";
import { TaskFilters } from "./task-filters";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { LoadingState, SideOverlay } from "../../components";
import { useGetFullProject } from "../../custom-hooks";
import { FTPBreadCrumbs } from "../../components-new";
import AssignTaskForm from "./task-list-components/AssignTaskForm";
import BoothMapView from "./task-list-components/booth-map-view";
import PageShell from "../../layouts/PageShell";
import { useLocalStorage } from "react-use";
import { ViewToggle, TaskFab, TaskSearchPanel } from "./task-components";

const TaskManager = observer((props) => {
    const { projectid } = useParams();
    const rootStore = useStore();
    const { taskManagerStore, projectStore } = rootStore;
    const { active_task, loading, task_filter_modal, show_assign_task_modal, toggle_assign_task_modal } =
        taskManagerStore;
    const { width } = useWindowSize();
    const isMobile = width <= 766;
    const [t] = useTranslation("task_manager");
    const { isLoading } = useGetFullProject();

    const [viewMode, setViewMode] = useLocalStorage<"card" | "table">("task-view-mode", "card");

    // Get total from store which comes from server response - MUST be before early return
    const [serverTotal, setServerTotal] = React.useState(0);

    const crumbs = [
        { name: "DashBoard", href: `/project/${projectid}`, current: true },
        { name: "Task Manager", href: "#", current: true },
    ];

    const { task_count_per_page, tasklist_current_page_number, filtered_task_list, task_list_query } =
        taskManagerStore;

    useEffect(() => {
        const active_project = JSON.parse(localStorage.getItem("__active_project"));
        taskManagerStore.set_surveys(safe_get(active_project, "surveys", []));
        fetch_users();
    }, [projectid]);

    useEffect(() => {
        taskManagerStore.set_task_edit_modal(false);
        taskManagerStore.set_task_view_modal(false);
        taskManagerStore.set_task_list_query("");
    }, [projectid]);

    React.useEffect(() => {
        // This will be updated when task list is fetched from server
        setServerTotal(taskManagerStore.task_list_total || 0);
    }, [taskManagerStore.task_list_total]);

    const fetch_users = async () => {
        const payload = {
            projectid: parseInt(projectid),
            tags: [],
            query: "",
            limit: 500,
            offset: 0,
        };
        const res = await projectStore.getAllUsers(payload);
        if (res.status === 200) {
            taskManagerStore.set_users(safe_get(res.data, "users", []));
        } else {
            toast.error("Users are not loaded, please refresh the page.");
        }
    };

    if (isLoading) return <LoadingState />;

    const totalPages = Math.ceil(serverTotal / task_count_per_page);

    // Calculate the range of items being shown
    const startIndex = (tasklist_current_page_number - 1) * task_count_per_page + 1;
    const endIndex = Math.min(tasklist_current_page_number * task_count_per_page, serverTotal);
    const resultsCount = filtered_task_list?.length || 0;
    const resultsSize = serverTotal;

    const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
        taskManagerStore.update_tm_store_prop("tasklist_current_page_number", page);
        window.sessionStorage.setItem("pageno", page.toString());
    };

    const handlePageSizeChange = (size: number) => {
        taskManagerStore.update_tm_store_prop("task_count_per_page", size);
        taskManagerStore.update_tm_store_prop("tasklist_current_page_number", 1);
    };

    const handleSearch = (query: string) => {
        taskManagerStore.set_task_list_query(query);
    };

    const handleClearAll = () => {
        // Clear search query
        taskManagerStore.set_task_list_query("");
        // Clear all filters
        taskManagerStore.update_task_filters({
            userList: [],
            pivLevel1: [],
            status: [],
            surveys: [],
        });
        // Reset to first page
        taskManagerStore.update_tm_store_prop("tasklist_current_page_number", 1);
    };

    // Check if there are active filters or search query
    const hasActiveFilters =
        task_list_query.trim() !== "" ||
        taskManagerStore.task_filters.userList.length > 0 ||
        taskManagerStore.task_filters.pivLevel1.length > 0 ||
        taskManagerStore.task_filters.status.length > 0 ||
        taskManagerStore.task_filters.surveys.length > 0;

    const headerActions = (
        <div className="flex items-center gap-3">
            {/* View Toggle - Desktop only */}
            <div className="hidden md:block">
                <ViewToggle view={viewMode} onChange={setViewMode} />
            </div>
            <button
                className="rounded-lg bg-gray-800 px-4 py-2 font-semibold text-white transition-colors hover:bg-gray-900"
                onClick={toggle_assign_task_modal}
            >
                Assign tasks
            </button>
        </div>
    );

    return (
        <>
            <PageShell
                title={"Task Manager"}
                headerActions={headerActions}
                breadcrumbs={
                    <div className="sticky top-[4rem] z-20 [&_nav]:!top-0 [&_nav]:mb-0 [&_nav]:!border-b-0 [&_nav]:bg-transparent [&_nav]:!shadow-none">
                        <FTPBreadCrumbs crumbs={crumbs} />
                    </div>
                }
                contentPadding="px-4"
            >
                <div className="px-4 py-4 md:px-6 lg:px-8">
                    <CreateTask />

                    {/* Search and Filter Panel */}
                    <TaskSearchPanel
                        pageSize={task_count_per_page}
                        pageNumber={tasklist_current_page_number}
                        totalPages={totalPages}
                        resultsSize={resultsSize}
                        startIndex={startIndex}
                        endIndex={endIndex}
                        onPageChange={handlePageChange}
                        onPageSizeChange={handlePageSizeChange}
                        onSearch={handleSearch}
                        onFilterClick={() => taskManagerStore.update_task_filter_modal(true)}
                        onClearAll={handleClearAll}
                        hasActiveFilters={hasActiveFilters}
                    />

                    {/* Task List */}
                    <TaskList viewMode={viewMode} />

                    {/* Floating Action Button */}
                    <TaskFab onClick={() => taskManagerStore.set_task_add_modal(true)} />

                    {/* Modals and Overlays */}
                    {!_.isEmpty(active_task) && <EditTask />}
                    {!_.isEmpty(active_task) && <TaskView />}

                    {/* Map Modal */}
                    {taskManagerStore.show_booth_map && !_.isEmpty(active_task) && (
                        <BoothMapView geoJson={null} task={active_task} />
                    )}

                    <SideOverlay
                        onClose={() => {
                            taskManagerStore.update_task_filter_modal(false);
                        }}
                        show={task_filter_modal}
                        children={<TaskFilters />}
                        title={t("task_filters")}
                    />
                    <SideOverlay
                        onClose={() => {
                            toggle_assign_task_modal();
                        }}
                        show={show_assign_task_modal}
                        children={<AssignTaskForm />}
                        title={"Assign Tasks"}
                        size="5xl"
                    />
                </div>
            </PageShell>
        </>
    );
});

export default TaskManager;
